import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { Calendar, Trash2, Edit2, X, Plus, ShoppingBasket, Banknote, BarChart3, List, Save } from 'lucide-react';
import { API_URL } from '../../api/config';

const ExpenseTracker = () => {
  const { 
    userToken: token, 
    isAuthenticated,
    isLoading: authLoading,
    signOut: authSignOut
  } = useAuth();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [expenses, setExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRange, setDeleteRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [stats, setStats] = useState({
    totalExpenses: 0,
    avgExpense: 0,
    maxExpense: 0,
    count: 0
  });
  const [dropdownStates, setDropdownStates] = useState({
    item: false,
    status: false,
    payment: false
  });
  const [formData, setFormData] = useState({
    item: '',
    quantity: '',
    price: '',
    totalPrice: '',
    status: 'Unpaid',
    modeOfPayment: 'None'
  });

  const items = [
    'Other',
    'Chicken', 'Mutton', 'Boneless Chicken', 'Boneless Mutton',
    'Kashmiri Spices', 'Spices', 'Rice', 'Milk', 'Dry fruits',
    'Water', 'Cold Drink', 'Cab/Indrive', 'Petrol', 'Diesel',
    'Coal', 'Cylinder', 'Paneer', 'Curd', 'Vegetables',
    'Labour', 'Marketing', 'Packaging', 'Rent',
    'Electricity Bill', 'Salary'
  ];
  
  const [customItem, setCustomItem] = useState('');
  const [showCustomItemInput, setShowCustomItemInput] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('7days');
  const [apiError, setApiError] = useState(null);
  
  const filterButtons = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: '7days', label: '7 Days'},
    { value: '30days', label: '30 Days' },
    { value: '3months', label: '3 Months'},
    { value: '6months', label: '6 Months'},
    { value: '1year', label: '1 Year'}
  ];

  const handleApiError = useCallback((error) => {
    console.error('API Error:', error);
    setApiError(error.message);
    
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      authSignOut();
    }
    
    setTimeout(() => setApiError(null), 5000);
  }, [authSignOut]);

  const getFilterDates = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate, endDate;
    
    switch(selectedFilter) {
      case 'today':
        startDate = new Date(today);
        endDate = new Date(now);
        break;
      case 'yesterday':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case '7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        endDate = new Date(now);
        break;
      case '30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        endDate = new Date(now);
        break;
      case '3months':
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 3);
        endDate = new Date(now);
        break;
      case '6months':
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 6);
        endDate = new Date(now);
        break;
      case '1year':
        startDate = new Date(today);
        startDate.setFullYear(startDate.getFullYear() - 1);
        endDate = new Date(now);
        break;
      default:
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        endDate = new Date(now);
        break;
    }
    
    return { startDate, endDate };
  }, [selectedFilter]);

  const fetchStats = useCallback(async () => {
    try {
      if (!isAuthenticated || !token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/stats`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      const result = await response.json();
      setStats(result.data || result);
    } catch (error) {
      handleApiError(error);
    }
  }, [token, isAuthenticated, handleApiError]);

  const fetchExpenses = useCallback(async () => {
    try {
      if (!isAuthenticated || !token) {
        throw new Error('Not authenticated');
      }

      const { startDate, endDate } = getFilterDates();
      const response = await fetch(
        `${API_URL}/expenses?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch expenses');
      }
      
      const result = await response.json();
      setExpenses(result.data || []);
    } catch (error) {
      handleApiError(error);
      setExpenses([]);
    }
  }, [token, getFilterDates, isAuthenticated, handleApiError]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    
    if (isAuthenticated && !authLoading) {
      fetchExpenses();
      fetchStats();
    }

    return () => clearInterval(timer);
  }, [fetchExpenses, fetchStats, isAuthenticated, authLoading]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!isAuthenticated || !token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          item: formData.item,
          quantity: formData.quantity || null,
          price: formData.price || null,
          totalPrice: parseFloat(formData.totalPrice),
          modeOfPayment: formData.modeOfPayment,
          status: formData.status
        })
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add expense');
      }
      
      await fetchExpenses();
      await fetchStats();
      resetForm();
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      if (!isAuthenticated || !token || !editingExpense) {
        throw new Error('Not authenticated or invalid expense');
      }

      const response = await fetch(`${API_URL}/expenses/${editingExpense.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          item: formData.item,
          quantity: formData.quantity || null,
          price: formData.price || null,
          totalPrice: parseFloat(formData.totalPrice),
          modeOfPayment: formData.modeOfPayment,
          status: formData.status
        })
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update expense');
      }
      
      await fetchExpenses();
      await fetchStats();
      resetForm();
      setEditingExpense(null);
    } catch (error) {
      handleApiError(error);
    }
  };

  const resetForm = () => {
    setFormData({
      item: '',
      quantity: '',
      price: '',
      totalPrice: '',
      status: 'Unpaid',
      modeOfPayment: 'None'
    });
    setShowCustomItemInput(false);
    setCustomItem('');
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      item: expense.item,
      quantity: expense.quantity || '',
      price: expense.price || '',
      totalPrice: expense.total_price || expense.totalPrice || '',
      status: expense.status,
      modeOfPayment: expense.mode_of_payment || expense.modeOfPayment || 'None'
    });
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      if (!isAuthenticated || !token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/expenses/${expenseId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete expense');
      }
      
      await fetchExpenses();
      await fetchStats();
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDeleteRange = async () => {
    try {
      if (!isAuthenticated || !token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/expenses-range`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          startDate: deleteRange.startDate,
          endDate: deleteRange.endDate
        })
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized - Please login again');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete expense range');
      }
      
      const data = await response.json();
      alert(`Successfully deleted ${data.deletedCount} expense records`);
      await fetchExpenses();
      await fetchStats();
      setShowDeleteModal(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDeleteLastMonth = async () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    
    setDeleteRange({
      startDate: lastMonth.toISOString().split('T')[0],
      endDate: lastMonthEnd.toISOString().split('T')[0]
    });
    setShowDeleteModal(true);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-semibold">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8 max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-4">Authentication Required</h2>
          <p className="text-slate-600 mb-6 font-medium">You need to be logged in to access the expense tracker.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold shadow-lg shadow-blue-100"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 lg:p-8">
      {/* Error Notification */}
      {apiError && (
        <div className="fixed top-4 right-4 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="bg-rose-50 border-l-4 border-rose-500 text-rose-700 p-4 rounded-xl shadow-lg max-w-md">
            <div className="flex justify-between items-start">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-rose-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="font-bold">Error: {apiError}</p>
              </div>
              <button 
                onClick={() => setApiError(null)} 
                className="text-rose-700 hover:text-rose-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[100rem] mx-auto space-y-8 animate-in fade-in duration-500">
        {/* Header Section */}
        <div className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Calendar size={16} />
              <span className="text-sm font-medium">{currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Daily Expenses</h1>
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
            {filterButtons.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  selectedFilter === filter.value 
                    ? 'bg-[#1E293B] text-white shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-sm font-semibold">Total Expenses</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2">₹{stats.totalExpenses?.toLocaleString() || 0}</h3>
              </div>
              <div className="p-3 rounded-xl bg-rose-50 text-rose-500">
                <Banknote size={20} />
              </div>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full mt-auto">
              <div className="h-full bg-rose-500 rounded-full w-[45%]"></div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-sm font-semibold">Average Expense</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2">₹{stats.avgExpense?.toFixed(2) || 0}</h3>
              </div>
              <div className="p-3 rounded-xl bg-orange-50 text-orange-500">
                <BarChart3 size={20} />
              </div>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full mt-auto">
              <div className="h-full bg-orange-500 rounded-full w-[45%]"></div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden flex flex-col justify-between h-36">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-slate-500 text-sm font-semibold">Total Entries</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2">{stats.count || 0}</h3>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 text-blue-500">
                <List size={20} />
              </div>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full mt-auto">
              <div className="h-full bg-blue-500 rounded-full w-[45%]"></div>
            </div>
          </div>
        </div>

        {/* Add/Edit Expense Form */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white">
              <Plus size={16} strokeWidth={3} />
            </div>
            <h2 className="text-lg font-bold text-slate-900">
              {editingExpense ? 'Edit Expense' : 'Add New Expense'}
            </h2>
          </div>
          
          <form onSubmit={editingExpense ? handleUpdate : handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
              {/* Item Selection */}
              <div className="md:col-span-5 relative">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Item Name / Description</label>
                <div className="relative">
                  <ShoppingBasket className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <button
                    type="button"
                    className="w-full pl-12 pr-4 py-3 text-left bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    onClick={() => setDropdownStates(prev => ({ ...prev, item: !prev.item }))}
                  >
                    {formData.item || "Select an item"}
                  </button>
                  {dropdownStates.item && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      {items.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition-colors"
                          onClick={() => {
                            if (item === 'Other') {
                              setShowCustomItemInput(true);
                              setDropdownStates(prev => ({ ...prev, item: false }));
                            } else {
                              handleInputChange('item', item);
                              setShowCustomItemInput(false);
                              setDropdownStates(prev => ({ ...prev, item: false }));
                            }
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Item Input */}
              {showCustomItemInput && (
                <div className="md:col-span-5">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Custom Item Name</label>
                  <input
                    type="text"
                    value={customItem}
                    onChange={(e) => {
                      setCustomItem(e.target.value);
                      handleInputChange('item', e.target.value);
                    }}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    placeholder="Enter custom item name"
                  />
                </div>
              )}

              {/* Quantity */}
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Quantity</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-center"
                  placeholder="1"
                />
              </div>

              {/* Price per unit */}
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Price / Unit</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="md:col-span-1 text-center font-bold text-slate-300 pb-4">=</div>

              {/* Total Price */}
              <div className="md:col-span-2">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Total</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input
                    type="number"
                    value={formData.totalPrice}
                    onChange={(e) => handleInputChange('totalPrice', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-50">
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="radio" 
                    checked={formData.status === 'Paid'}
                    onChange={() => handleInputChange('status', 'Paid')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300" 
                  />
                  <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900">Paid</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="radio" 
                    checked={formData.status === 'Unpaid'}
                    onChange={() => handleInputChange('status', 'Unpaid')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300" 
                  />
                  <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900">Unpaid</span>
                </label>

                {formData.status === 'Paid' && (
                  <div className="relative ml-4">
                    <button
                      type="button"
                      className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-600 hover:bg-white transition-all"
                      onClick={() => setDropdownStates(prev => ({ ...prev, payment: !prev.payment }))}
                    >
                      {formData.modeOfPayment || "Payment Mode"}
                    </button>
                    {dropdownStates.payment && (
                      <div className="absolute z-10 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg">
                        {['Cash', 'UPI', 'Card'].map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition-colors"
                            onClick={() => {
                              handleInputChange('modeOfPayment', mode);
                              setDropdownStates(prev => ({ ...prev, payment: false }));
                            }}
                          >
                            {mode}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                {editingExpense && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingExpense(null);
                      resetForm();
                    }}
                    className="px-6 py-4 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-all"
                  >
                    Cancel
                  </button>
                )}
                <button 
                  type="submit"
                  className="flex items-center gap-2 bg-[#2563eb] text-white px-8 py-4 rounded-xl font-bold shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all transform hover:scale-[1.02] active:scale-95"
                >
                  <Save size={18} />
                  {editingExpense ? 'Update Entry' : 'Add Entry'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Recent Expenses Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 flex items-center justify-between border-b border-slate-100 bg-slate-50/50">
            <h3 className="text-lg font-bold text-slate-900">Recent Expenses</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-white transition-all"
              >
                <Calendar size={14} />
                Delete Range
              </button>
              <button
                onClick={handleDeleteLastMonth}
                className="flex items-center gap-2 px-4 py-2 border border-rose-100 bg-rose-50 rounded-lg text-xs font-bold text-rose-600 hover:bg-rose-100 transition-all"
              >
                <Trash2 size={14} />
                Delete Last Month
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-8 py-5">Item Details</th>
                  <th className="px-6 py-5">Qty</th>
                  <th className="px-6 py-5">Price</th>
                  <th className="px-6 py-5">Total</th>
                  <th className="px-6 py-5">Status</th>
                  <th className="px-6 py-5 text-right">Date</th>
                  <th className="px-8 py-5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm font-medium text-slate-600">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/30 transition-colors group">
                    <td className="px-8 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 text-blue-600">
                          <ShoppingBasket size={18} />
                        </div>
                        <span className="font-bold text-slate-800">{expense.item}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{expense.quantity || '-'}</td>
                    <td className="px-6 py-4 text-slate-500 font-semibold">{expense.price ? `₹${expense.price}` : '-'}</td>
                    <td className="px-6 py-4 font-extrabold text-slate-900">₹{expense.total_price || expense.totalPrice}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold ${
                        expense.status === 'Paid' 
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                          : 'bg-rose-50 text-rose-600 border border-rose-100'
                      }`}>
                        {expense.status}
                        {(expense.mode_of_payment || expense.modeOfPayment) !== 'None' && 
                         ` (${expense.mode_of_payment || expense.modeOfPayment})`}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-slate-400 text-xs font-semibold">
                      {new Date(expense.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-8 py-4">
                      <div className="flex items-center justify-center gap-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEdit(expense)}
                          className="p-2 hover:bg-slate-100 hover:text-[#4F46E5] rounded-lg transition-all"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-2 hover:bg-rose-50 hover:text-rose-500 rounded-lg transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {expenses.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500 font-medium">No expenses found for the selected period</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex flex-col justify-between items-start mb-6">
              <div className="flex justify-between items-center w-full">
                <h3 className="text-xl font-extrabold text-rose-600">
                  Delete Expenses by Date Range
                </h3>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-slate-400 hover:text-slate-700 p-1 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl w-full">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ <strong>Warning:</strong> This action cannot be undone.
                  Selected expenses will be permanently deleted.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Start Date</label>
                <input
                  type="date"
                  value={deleteRange.startDate}
                  onChange={(e) => setDeleteRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">End Date</label>
                <input
                  type="date"
                  value={deleteRange.endDate}
                  onChange={(e) => setDeleteRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
              </div>
              <div className="pt-4 space-y-3">
                <button
                  onClick={handleDeleteRange}
                  className="w-full px-6 py-4 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all font-bold shadow-lg shadow-rose-100"
                >
                  Delete Expenses
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full px-6 py-4 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all font-bold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTracker;